import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, CircularProgress, Backdrop, Avatar, TextField, Divider, MenuItem, FormControl, Select, Button, Stepper, Step, StepLabel, Checkbox, FormControlLabel, Autocomplete } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery, useMutation } from 'react-query';
import HomeHeader from '../../Components/HomeHeader';
import HomeFooter from '../../Components/HomeFooter';
import AlertDialog from '../../Components/AlertDialog';
import useAlertDialog from '../../Components/useAlertDialog';
import client from '../../API';
import defaultPfp from '../../Components/globalAssets/ppfLogo.png';
import { validateEmail, validatePassword, capitalizeFirstLetter } from '../../Utils';
import routes from '../../Config/routes';

/* ---------------------- Style Helpers & Constants ---------------------- */
const Frame = {
  position: 'relative', width: '100%', height: '100vh', overflow: 'hidden',
  display: 'flex', justifyContent: 'center', alignItems: 'center'
};
const SlideBox = (active) => ({
  position: 'absolute', inset: 0, padding: 'clamp(1rem, 5vw, 2rem)',
  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
  textAlign: 'center', color: '#fff', opacity: active ? 1 : 0, transition: 'opacity 0.8s',
  overflowY: 'auto', maxHeight: '100vh'
});
const NavDots = { position: 'absolute', bottom: '1.5rem', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 12, zIndex: 20 };
const Dot = (theme, active) => ({
  width: 12, height: 12, borderRadius: '50%', cursor: 'pointer',
  background: active ? theme.palette.primary.main : `${theme.palette.primary.main}55`,
  transition: 'transform 0.25s', transform: active ? 'scale(1.3)' : 'scale(1)', '&:hover': { transform: 'scale(1.3)' }
});
const GradientButton = {
  background: 'linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)', color: 'white', fontWeight: 'bold',
  padding: { xs: '10px 20px', sm: '12px 24px' }, borderRadius: '12px', fontSize: { xs: '0.9rem', sm: '1rem' },
  minWidth: { xs: '120px', sm: '140px' }, '&:hover': {
    transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(99,102,241,0.4)',
    background: 'linear-gradient(45deg, #6366f1 30%, #8b5cf6 90%)'
  }
};
const ModernInput = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px', transition: 'all 0.3s ease', color: '#fff', backgroundColor: 'rgba(255,255,255,0.1)',
    minHeight: { xs: '48px', sm: '56px' },
    '& .MuiSelect-select': { color: '#fff' }, '& .MuiSvgIcon-root': { color: '#fff' },
    '& .MuiInputBase-input': { color: '#fff', fontSize: { xs: '0.9rem', sm: '1rem' } },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#fff', boxShadow: '0 0 0 2px rgba(255,255,255,0.2)' }
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.9rem', sm: '1rem' } },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' }
};
const StyledCheckbox = {
  color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#fff' },
  '& .MuiSvgIcon-root': { fontSize: { xs: '1.2rem', sm: '1.5rem' } }
};
const StyledStepIcon = (theme, ownerState) => ({
  width: { xs: 20, sm: 24 }, height: { xs: 20, sm: 24 }, display: 'flex', borderRadius: '50%', alignItems: 'center', justifyContent: 'center',
  backgroundColor: ownerState.completed ? '#6366f1' : '#e2e8f0', color: ownerState.completed ? 'white' : theme.palette.text.secondary,
  fontWeight: 600, fontSize: { xs: '0.8rem', sm: '1rem' }
});
const SLIDE_MS = 3500;

const industryOptions = [
  'Fashion', 'Tech', 'Food', 'Beauty', 'Sports', 'Travel', 'Finance', 'Health',
  'Education', 'Entertainment', 'Automotive', 'Real Estate', 'Hospitality',
  'Retail', 'Manufacturing', 'Telecommunications', 'Marketing Agency'
];
const countryOptions = [
  'United States', 'China', 'Canada', 'United Kingdom', 'Australia', 'Germany',
  'France', 'Italy', 'Spain', 'Japan', 'India', 'Brazil', 'Mexico', 'Russia',
  'South Korea', 'South Africa'
];

/* --------------------------- Register Form --------------------------- */
const BrandRegisterForm = ({ conversationIdParam, inviteEmailParam }) => {
  const navigate = useNavigate();
  const { ref } = useParams();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const referrer_id = ref && !isNaN(Number(ref)) ? Number(ref) : null;
  const referralSource = referrer_id === null ? ref || '' : '';
  const [conversationId] = useState(conversationIdParam || null);

  const [userInfo, setUserInfo] = useState({
    firstName: '', lastName: '', email: inviteEmailParam || '', password: '', confirmPassword: '',
    companyName: '', streetAddress: '', city: '', state: '', country: '', phoneNumber: '',
    industry: '', bio: '', businessType: '', isAgency: '', monthlySpend: '', teamAccounts: '',
    paymentMethod: '', referralSource, referrer_id, termsAccepted: false
  });

  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Personal Information', 'Company Information', 'Business Details'];

  const { data: companiesData = [], isLoading: isCompaniesLoading } = useQuery(['companiesList'], client.companies.list);

  const { mutate: register, isLoading } = useMutation(client.companies.demoRegister, {
    onSuccess: () => navigate(routes.login),
    onError: (error) => {
      let errorMessage = 'An error occurred, please try again.';
      if (error.code === 'ERR_NETWORK') errorMessage = 'Network disconnected!';
      else if (error.response?.data) errorMessage = error.response.data.error || errorMessage;
      openDialog('Error', errorMessage, closeDialog, closeDialog, 'Ok');
    }
  });

  /* ------------------------- Handlers ------------------------- */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserInfo((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleBack = () => setActiveStep((p) => p - 1);
  const handleNext = () => {
    const required = getRequiredFieldsForStep(activeStep);
    let valid = true;
    required.forEach((f) => {
      if (valid && (!userInfo[f] || userInfo[f].trim() === '')) {
        openDialog('Error', `${capitalizeFirstLetter(f)} field is required!`, closeDialog, closeDialog, 'Ok');
        valid = false;
      }
    });
    if (!valid) return;

    if (activeStep === 0) {
      if (!validateEmail(userInfo.email)) { openDialog('Error', 'Invalid email!', closeDialog, closeDialog, 'Ok'); return; }
      if (!validatePassword(userInfo.password)) { openDialog('Error', 'Password needs 6+ characters!', closeDialog, closeDialog, 'Ok'); return; }
      if (userInfo.password !== userInfo.confirmPassword) { openDialog('Error', 'Passwords do not match!', closeDialog, closeDialog, 'Ok'); return; }
    }

    if (activeStep === steps.length - 1) {
      if (!userInfo.termsAccepted) { openDialog('Error', 'Please accept the Terms & Conditions to continue.', closeDialog, closeDialog, 'Ok'); return; }
      register(userInfo);
    } else setActiveStep((p) => p + 1);
  };

  /* ---------------------- Validation Helpers ---------------------- */
  const getRequiredFieldsForStep = (step) => {
    if (step === 0) return ['firstName', 'lastName', 'email', 'password', 'confirmPassword', 'phoneNumber'];
    if (step === 1) return ['companyName', 'streetAddress', 'city', 'state', 'country', 'industry', 'bio'];
    if (step === 2) return ['businessType', 'isAgency', 'monthlySpend', 'teamAccounts', 'paymentMethod'];
    return [];
  };

  /* ------------------------- Menus ------------------------- */
  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: '#1a1a1a', border: '1px solid #fff', borderRadius: '8px',
        '& .MuiMenuItem-root': {
          color: '#fff', fontSize: { xs: '0.9rem', sm: '1rem' },
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
          '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' }
        }
      }
    }
  };

  /* --------------------- Step Content --------------------- */
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {['firstName', 'lastName', 'email', 'password', 'confirmPassword', 'phoneNumber'].map((field, i) => (
              <TextField key={field} sx={ModernInput} disabled={isLoading} label={capitalizeFirstLetter(field.replace('confirmPassword','Re-enter Password').replace('phoneNumber','Phone Number'))}
                type={field.toLowerCase().includes('password') ? 'password' : 'text'} name={field} value={userInfo[field]} onChange={handleChange} required fullWidth />
            ))}
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete freeSolo loading={isCompaniesLoading} options={companiesData.map((c) => c.name)}
              value={userInfo.companyName}
              onChange={(e, v) => setUserInfo((p) => ({ ...p, companyName: v || '' }))}
              onInputChange={(e, v) => setUserInfo((p) => ({ ...p, companyName: v || '' }))}
              renderInput={(params) => <TextField {...params} sx={ModernInput} label="Company Name" required disabled={isLoading} fullWidth />} PopperComponent={(props) => <Box {...props} sx={{ '& .MuiAutocomplete-paper': { backgroundColor: '#1a1a1a', border: '1px solid #fff', borderRadius: '8px', '& .MuiAutocomplete-option': { color: '#fff', fontSize: { xs: '0.9rem', sm: '1rem' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }, '&[aria-selected="true"]': { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' } } } }} />} />
            {['streetAddress','city','state'].map((f)=>(<TextField key={f} sx={ModernInput} disabled={isLoading} label={capitalizeFirstLetter(f)} name={f} value={userInfo[f]} onChange={handleChange} required fullWidth/>))}
            <FormControl fullWidth required>
              <Select sx={ModernInput} name="country" value={userInfo.country} onChange={handleChange} displayEmpty renderValue={(v)=>(<Typography sx={{color:v?'#fff':'rgba(255,255,255,0.7)'}}>{v||'Select your country'}</Typography>)} MenuProps={menuProps}>{countryOptions.map((o)=><MenuItem key={o} value={o}>{o}</MenuItem>)}</Select>
            </FormControl>
            <FormControl fullWidth required>
              <Select sx={ModernInput} name="industry" value={userInfo.industry} onChange={handleChange} displayEmpty renderValue={(v)=>(<Typography sx={{color:v?'#fff':'rgba(255,255,255,0.7)'}}>{v||'Select your industry'}</Typography>)} MenuProps={menuProps}>{industryOptions.map((o)=><MenuItem key={o} value={o}>{o}</MenuItem>)}</Select>
            </FormControl>
            <TextField sx={ModernInput} disabled={isLoading} label="Tell us about your company" name="bio" multiline rows={4} value={userInfo.bio} onChange={handleChange} required fullWidth />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {['businessType','isAgency'].map((f,i)=>(
              <FormControl key={f} fullWidth required>
                <Select sx={ModernInput} name={f} value={userInfo[f]} onChange={handleChange} displayEmpty renderValue={(v)=>(<Typography sx={{color:v?'#fff':'rgba(255,255,255,0.7)'}}>{v|| (f==='businessType'? 'Is your business all under one company, or is it separate?':'Are you an agency?')}</Typography>)} MenuProps={menuProps}>
                  {f==='businessType' ? [<MenuItem value="One Company" key="one">One Company</MenuItem>,<MenuItem value="Separate" key="sep">Separate</MenuItem>] : [<MenuItem value="Yes" key="yes">Yes</MenuItem>,<MenuItem value="No" key="no">No</MenuItem>]}
                </Select>
              </FormControl>))}
            {['monthlySpend','teamAccounts'].map((f)=>(<TextField key={f} sx={ModernInput} disabled={isLoading} label={f==='monthlySpend'?'Expected monthly spend':'Number of team accounts'} name={f} value={userInfo[f]} onChange={handleChange} required fullWidth/>))}
            <FormControl fullWidth required>
              <Select sx={ModernInput} name="paymentMethod" value={userInfo.paymentMethod} onChange={handleChange} displayEmpty renderValue={(v)=>(<Typography sx={{color:v?'#fff':'rgba(255,255,255,0.7)'}}>{v||'Main payment method'}</Typography>)} MenuProps={menuProps}>
                {['Credit Card','Bank Transfer','PayPal'].map((m)=><MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField sx={ModernInput} disabled={isLoading} label="How did you hear about us?" name="referralSource" value={userInfo.referralSource} onChange={handleChange} fullWidth />
            <FormControlLabel control={<Checkbox name="termsAccepted" checked={userInfo.termsAccepted} onChange={handleChange} disabled={isLoading} sx={StyledCheckbox}/>} label={<Typography sx={{color:'#fff',fontSize:{xs:'0.85rem',sm:'0.9rem'}}}>I agree to the <RouterLink to="/terms" target="_blank" rel="noopener" style={{color:'#6366f1'}}>Terms & Conditions</RouterLink></Typography>} sx={{mt:2}}/>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  /* --------------------------- Render --------------------------- */
  return (
    <Box sx={{ width:'100%', maxWidth:{xs:'100%', sm:700}, mx:'auto', backgroundColor:'#000', color:'#fff', p:{xs:2, sm:3}, borderRadius:'16px', '& .MuiStepper-root':{backgroundColor:'#000','& .MuiStepLabel-label':{color:'#fff',fontWeight:600,fontSize:{xs:'0.8rem',sm:'0.9rem'}}},'& .MuiStepConnector-line':{borderColor:'rgba(255,255,255,0.3)'},'& .MuiStepLabel-iconContainer':{backgroundColor:'#000'}}}>
      <Stepper activeStep={activeStep} sx={{ mb:{xs:4, sm:6} }}>
        {steps.map((label)=>(
          <Step key={label}>
            <StepLabel StepIconComponent={({completed})=><Box sx={StyledStepIcon(useTheme(),{completed})}>{completed?'✓':label.charAt(0)}</Box>} />
          </Step>
        ))}
      </Stepper>
      <Box sx={{ p:{xs:1, sm:4}, borderRadius:'16px', border:'1px solid rgba(255,255,255,0.2)' }}>
        {getStepContent(activeStep)}
        <Box sx={{ display:'flex', justifyContent:'space-between', mt:{xs:3, sm:4}, pt:2, borderTop:'1px solid rgba(255,255,255,0.2)' }}>
          <Button onClick={handleBack} disabled={activeStep===0||isLoading} sx={{ borderRadius:'8px', color:'#fff', border:'1px solid rgba(255,255,255,0.3)', fontSize:{xs:'0.9rem',sm:'1rem'}, minWidth:{xs:'100px',sm:'120px'}, '&:hover':{ backgroundColor:'rgba(255,255,255,0.1)' } }}>Back</Button>
          <Button sx={GradientButton} onClick={handleNext} disabled={isLoading || (activeStep===steps.length-1 && !userInfo.termsAccepted)}>
            {activeStep===steps.length-1?'Create Account':'Continue'}
            {isLoading && <CircularProgress size={20} sx={{ ml:1, color:'#fff' }} />}
          </Button>
        </Box>
      </Box>
      <AlertDialog alertState={dialogState} />
    </Box>
  );
};

/* --------------------------- Presentation Wrapper --------------------------- */
export default function NewUserPresentation() {
  const navigate = useNavigate();
  const theme = useTheme();
  const timerRef = useRef(null);
  const { referrer } = useParams();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const [loading, setLoading] = useState(false);
  const [referrerData, setReferrerData] = useState(null);

  useEffect(() => {
    if (!referrer) return;
    (async () => {
      try {
        const res = await client.users.fetchPublicDetails(referrer);
        setReferrerData({
          name: `${res.first_name||''} ${res.last_name||''}`.trim()||res.username||'Unknown User',
          avatar: res.phphref || defaultPfp
        });
      } catch {
        setReferrerData({ name: 'Unknown User', avatar: defaultPfp });
      }
    })();
  }, [referrer]);

  /* ------------------------- Slides ------------------------- */
  const renderFormSlide = useCallback(()=> (
    <Box sx={{ width:'100%', maxWidth:{xs:'100%', sm:700}, mx:'auto', color:'#fff', px:{xs:1, sm:0} }}>
      <Typography variant="h3" sx={{ mb:1, fontWeight:700, textAlign:'center', color:'#fff', fontSize:{xs:'1.8rem', sm:'2.5rem'} }}>Company Onboarding</Typography>
      <Typography variant="body1" sx={{ opacity:0.85, mb:3, maxWidth:{xs:300, sm:480}, mx:'auto', textAlign:'center', color:'#fff', fontSize:{xs:'0.9rem', sm:'1rem'} }}>Join Blitz to manage campaigns & connect with top creators. Complete the form to get started.</Typography>
      <BrandRegisterForm />
    </Box>
  ), []);

  const slides = useMemo(()=>[
    { bg:'linear-gradient(135deg, #6d00ff, #2d0b63)', custom:(<Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
      <Avatar src={referrerData?.avatar||defaultPfp} sx={{ width:{xs:100, sm:140}, height:{xs:100, sm:140}, mb:1, border:'3px solid #fff' }} />
      <Typography variant="h2" sx={{ fontWeight:800, lineHeight:1.1, fontSize:{xs:'1.8rem', sm:'clamp(2rem, 6vw, 3.5rem)'} }}>Welcome to the Future!</Typography>
      {referrerData && <Typography variant="h5" sx={{ opacity:0.85, maxWidth:{xs:300, sm:700}, mb:1, fontSize:{xs:'1.2rem', sm:'1.5rem'} }}>{referrerData.name} INVITED YOU</Typography>}
      <Typography variant="h5" sx={{ opacity:0.85, maxWidth:{xs:300, sm:700}, mb:1, fontSize:{xs:'1.2rem', sm:'1.5rem'} }}>Join the <strong>Blitz Business Program</strong></Typography>
    </Box>) },
    { bg:'linear-gradient(135deg, #ff4d4d, #7000ff)', title:'Streamlined Campaign Management', bullets:['AI-powered creator matching','Automated contract generation','Real-time campaign analytics'] },
    { bg:'linear-gradient(135deg, #00c9ff, #005bff)', title:'Connect with Top Creators', bullets:['Access global creator network','Secure payment processing','Dedicated support team'] },
    { bg:'linear-gradient(135deg, #181818, #000)', custom: renderFormSlide() }
  ], [renderFormSlide, referrerData]);

  const [idx,setIdx] = useState(0);
  useEffect(()=>{ if(idx===slides.length-1) return; timerRef.current=setInterval(()=>setIdx(p=>(p+1)%slides.length), SLIDE_MS); return()=>clearInterval(timerRef.current); },[idx,slides.length]);

  /* --------------------------- Render --------------------------- */
  return (<>
    <HomeHeader />
    <Box sx={Frame}>{slides.map((s,i)=>(<Box key={i} sx={SlideBox(i===idx)} style={{ background:s.bg }}>
      {s.custom||(<> <Typography variant="h2" sx={{ fontSize:{xs:'1.8rem', sm:'clamp(2rem, 6vw, 3.5rem)'}, fontWeight:800, lineHeight:1.1 }}>{s.title}</Typography>
        {s.bullets && <Box component="ul" sx={{ listStyle:'none', p:0, m:0, display:'flex', flexDirection:'column', gap:1, maxWidth:{xs:300, sm:620} }}>{s.bullets.map((b)=>(<Typography component="li" key={b} sx={{ '&::before':{ content:'"•"', mr:1, color:theme.palette.primary.light }, fontSize:{xs:'0.9rem', sm:'clamp(1rem, 2.5vw, 1.25rem)'} }}>{b}</Typography>))}</Box>}
      </>)}</Box>))}<Box sx={NavDots}>{slides.map((_,i)=>(<Box key={i} sx={Dot(theme,i===idx)} onClick={()=>setIdx(i)} />))}</Box></Box>
    <HomeFooter />
    <Backdrop sx={{ color:'#fff', zIndex:(t)=>t.zIndex.drawer+1 }} open={loading}><CircularProgress color="inherit" /></Backdrop>
    <AlertDialog alertState={dialogState} />
  </>);
}
